import React from 'react'
import { atom, useRecoilValue, useSetRecoilState } from 'recoil'

export type MobileHeaderActionNode = {
  key: string
  element: React.ReactNode
}

export type MobilePageHeaderState = {
  title: string | React.ReactNode
  toggleActionKey: string | null
  enableFilter?: boolean
  actionNodes: MobileHeaderActionNode[]
  showShadow?: boolean
}

const initialMobilePageHeaderState: MobilePageHeaderState = {
  title: '',
  toggleActionKey: null,
  actionNodes: [],
  showShadow: false,
}

export const mobilePageHeaderState = atom<MobilePageHeaderState>({
  key: 'mobilePageHeaderState',
  default: initialMobilePageHeaderState,
})

export function useMobilePageHeader() {
  return useRecoilValue(mobilePageHeaderState)
}

export function useMobilePageHeaderSetter(options?: {
  resetOnUnmount?: boolean
}) {
  const setHeader = useSetRecoilState(mobilePageHeaderState)

  const { resetOnUnmount = true } = options || {}

  React.useLayoutEffect(() => {
    return () => {
      if (resetOnUnmount) {
        setHeader(initialMobilePageHeaderState)
      }
    }
  }, [])

  return {
    setToogleKey: (key: string | null) =>
      setHeader(prev => ({ ...prev, toggleActionKey: key })),
    setActionNodes: (actionNodes: MobileHeaderActionNode[]) =>
      setHeader(prev => ({ ...prev, actionNodes })),
    setEnableFilter: (enableFilter?: boolean) =>
      setHeader(prev => ({ ...prev, enableFilter })),
    setTitle: (title: string | React.ReactNode) =>
      setHeader(prev => ({ ...prev, title })),
    setShowShadow: (showShadow: boolean) =>
      setHeader(prev => ({ ...prev, showShadow })),
    resetHeader: () => setHeader(initialMobilePageHeaderState),
  }
}

export function useSetMobilePageHeader({
  title,
  toggleActionKey,
  actionNodes,
}: MobilePageHeaderState) {
  const setHeader = useSetRecoilState(mobilePageHeaderState)

  React.useLayoutEffect(() => {
    setHeader({ title, toggleActionKey, actionNodes })

    return function cleanup() {
      setHeader({
        title: '',
        toggleActionKey: null,
        actionNodes: [],
      })
    }
  }, [actionNodes, setHeader, title, toggleActionKey])

  return {
    setToogleKey: (key: string | null) =>
      setHeader(prev => ({ ...prev, toggleActionKey: key })),
  }
}

export function useSetMobilePageHeaderActionNodes(
  actionNodes: MobileHeaderActionNode[]
) {
  const setHeader = useSetRecoilState(mobilePageHeaderState)

  React.useLayoutEffect(() => {
    setHeader(prev => ({ ...prev, actionNodes }))

    return function cleanup() {
      setHeader(prev => ({ ...prev, actionNodes: [] }))
    }
  }, [actionNodes, setHeader])
}

export function useSetMobilePageHeaderFilter(enableFilter?: boolean) {
  const setHeader = useSetRecoilState(mobilePageHeaderState)
  React.useLayoutEffect(() => {
    setHeader(prev => ({ ...prev, title: '', enableFilter }))

    return function cleanup() {
      setHeader(prev => ({ ...prev, title: '', enableFilter: false }))
    }
  }, [enableFilter, setHeader])
}
